import React from "react"

import {
    Grid,
    GridCell,
    Select,
    TextInput,
} from "@bdfx/mergado-ui-kit"

import trans from "trans"
import { NumericInput } from "./fields"
import TransparentColor from "./transparent-color"
import { DEFAULT_LINE_CAP, DEFAULT_LINE_JOIN, LINE_CAP_CONSTS, LINE_JOIN_CONSTS, previewFile } from "./helpers"
import { Gradient } from "app/types/model-types"
import SelectGradient from "./select-gradient"
import Shape, { BEZIER_TYPE, CIRCLE_TEXT_TYPE, CIRCLE_TYPE, CURVED_TEXT_TYPE, DYNAMIC_TEXT_TYPE, ELLIPSE_TYPE, IMAGE_TYPE, LINE_TYPE, NONE_TYPE, POLYGON_TYPE, RATING_TYPE, RECTANGLE_EACH_LINE_TYPE, RECTANGLE_TYPE, TEXT_INSIDE_SHAPE_TYPE, TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE } from "shape-entities/types/shape"
import { find } from "lodash"
import { Option as SelectOption} from "react-select-item";
import ReactSelect from "react-select"
import { URL } from "app/config/api-config"
import { Option } from "@bdfx/mergado-ui-kit/lib/components/Form/Select"
import { SizeType } from "products/components/filters"
import calcBackgroundSize from "shape-edit/helpers/calcBackgroundSize"


const { min, max, abs, sin, cos, PI, round, atan2, sqrt, ceil } = Math

const ShapeStrokeForm = ({ form, formName, shape, activeShapeId, change, handleChangeGradient }) => {
    const subFormName = `shapes.${activeShapeId}`

    const allowTextBackground = [CIRCLE_TYPE, RECTANGLE_TYPE, RECTANGLE_EACH_LINE_TYPE, ELLIPSE_TYPE].includes(shape.text_background_type) &&
        [DYNAMIC_TEXT_TYPE, TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE].includes(shape.type)
    const allowBackgroundType = [DYNAMIC_TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, TEXT_TYPE].includes(shape.type)
    const allowStroke = [CIRCLE_TYPE, RECTANGLE_TYPE, RECTANGLE_EACH_LINE_TYPE, ELLIPSE_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE, DYNAMIC_TEXT_TYPE, TEXT_INSIDE_SHAPE_TYPE, TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, POLYGON_TYPE, BEZIER_TYPE, CURVED_TEXT_TYPE, LINE_TYPE, CIRCLE_TEXT_TYPE, RATING_TYPE].includes(shape.type)

    if (!allowStroke) {
        return null
    }

    const linecapOptions: SelectOption[] = LINE_CAP_CONSTS.filter((name, i) => i > 0).map((name, index) => {
        return {
            label: <Grid cols={'auto min-content'}>
                <GridCell>{trans(`line_cap_${name}`)}</GridCell>
                <GridCell><img src={`${URL.publicPath}img/icons/line_cap_${name}.svg`} className={'line-cap'} /></GridCell>
            </Grid>,
            value: `${index + 1}`,
        }
    })

    const linejoinOptions: SelectOption[] = LINE_JOIN_CONSTS.filter((name, i) => i > 0).map((name, index) => {
        return {
            label: <Grid cols={'auto min-content'}>
                <GridCell>{trans(`line_join_${name}`)}</GridCell>
                <GridCell><img src={`${URL.publicPath}img/icons/line_join_${name}.svg`} className={'line-join'} /></GridCell>
            </Grid>,
            value: `${index + 1}`,
        }
    })

    const selectedLinecap = shape.line_cap ? find(linecapOptions, (option: SelectOption) => {
        return `${shape.line_cap}` === `${option.value}`
    }) : null

    const selectedLinejoin = shape.line_join ? find(linejoinOptions, (option: SelectOption) => {
        return `${shape.line_join}` === `${option.value}`
    }) : null

    const strokeColor = shape.stroke_color ?? '#ffffff'

    const backgroundTypeOptions: Option[] = [NONE_TYPE, CIRCLE_TYPE, ELLIPSE_TYPE, RECTANGLE_TYPE, RECTANGLE_EACH_LINE_TYPE, IMAGE_TYPE].map(o => ({ label: trans('background_type_' + o), value: o }))

    const handleBackgroundTypeChanged = (backgroundType: string, targetShape: Shape, subFormName: string) => {
        const fromNoneToRectangle = targetShape.text_background_type === NONE_TYPE && backgroundType === RECTANGLE_TYPE
        const calculatedSize: SizeType = !fromNoneToRectangle ? calcBackgroundSize(targetShape) : { width: targetShape.width, height: targetShape.height }
        const m = max(calculatedSize.width, calculatedSize.height)

        const newSize: SizeType = backgroundType === CIRCLE_TYPE ? {
            width: m,
            height: m
        } : {
            width: max(targetShape.width, calculatedSize.width),
            height: max(targetShape.height, calculatedSize.height)
        }

        change(formName, subFormName, {
            ...targetShape,
            ...newSize,
            text_background_type: backgroundType,
        })
    }

    const gridcols = shape.type === RATING_TYPE ? 2 : 3

    return (
        <React.Fragment>
            <GridCell style={{alignSelf: 'center'}}>{trans('stroke')}</GridCell>
            <GridCell>
                <Grid cols={`repeat(${gridcols}, 1fr)`} gap={'5px'}>
                    <GridCell title={trans('stroke_color')}>
                        <TransparentColor
                            name={`${formName}.${subFormName}.stroke_color`}
                            value={strokeColor}
                            changeValue={(color: string) => change(formName, `${subFormName}.stroke_color`, color)}
                            hideGradients={true}
                        />
                    </GridCell>

                    <GridCell title={trans('stroke_width')}>
                        <NumericInput
                            formName={formName}
                            fieldName={'stroke_width'}
                            placeholder={'stroke_width'}
                            shapeIndex={shape.index}
                            id={'stroke-input'}
                            min={0}
                            max={100}
                            step={1}
                            units={'px'}
                            value={round(shape.stroke_width)}
                            onlyPositive={true}
                            onChangeFunction={(value: number) => {
                                if (value) {
                                    if (!shape.text_background_type) {
                                        change(formName, `${subFormName}.text_background_type`, RECTANGLE_TYPE)
                                    }
                                    if (!shape.stroke_color) {
                                        change(formName, `${subFormName}.stroke_color`, '#ffffff')
                                    }
                                } else {
                                    change(formName, `${subFormName}`, {
                                        ...shape,
                                        stroke_width: null,
                                        text_background_type: null,
                                        stroke_color: null,
                                    })

                                }
                            }}
                        />
                    </GridCell>

                    {allowBackgroundType &&
                        <GridCell title={trans('background_type')}>
                            <Select
                                name={`${formName}.${subFormName}.text_background_type`}
                                options={backgroundTypeOptions}
                                onChange={(evt) => handleBackgroundTypeChanged(evt, shape, subFormName)}
                            />
                        </GridCell>
                    }

                    {shape.text_background_type === IMAGE_TYPE &&
                        <GridCell title={trans("file")}>
                            <TextInput
                                id={`file_input_${shape.id}`}
                                type="file"
                                name={`${formName}.${subFormName}.file`}
                                value={shape.file}
                                onChange={(evt) => {
                                    previewFile(formName, `file_input_${shape.id}`, subFormName, shape, change, evt)
                                }}
                            />
                        </GridCell>
                    }

                    {allowTextBackground &&
                        <React.Fragment>
                            <SelectGradient
                                gradientId={shape.gradient_id}
                                handleChangeGradient={(gradient: Gradient) => handleChangeGradient(gradient, shape)}
                            />
                            <GridCell title={trans('text_background_color')}>
                                <TransparentColor
                                    name={`${formName}.${subFormName}.text_background_color`}
                                    value={shape['text_background_color'] || '#ffffff'}
                                    changeValue={(color: string) => {
                                        change(formName, `${subFormName}.text_background_color`, color)
                                    }}
                                    changeGradient={(gradient: { id: any }) => {
                                        const updatedShape = {
                                            ...shape,
                                            gradient_id: gradient.id,
                                            gradient: gradient,
                                        }
                                        change(formName, subFormName, updatedShape)
                                    }}
                                />
                            </GridCell>
                        </React.Fragment>
                    }

                    {(shape.type === RECTANGLE_TYPE || (allowTextBackground && [RECTANGLE_TYPE, RECTANGLE_EACH_LINE_TYPE].includes(shape.text_background_type))) &&
                        <GridCell title={trans('corner_radius')}>
                            <NumericInput
                                formName={formName}
                                fieldName={'corner_radius'}
                                placeholder={'corner_radius'}
                                shapeIndex={shape.index}
                                id={'corner_radius'}
                                min={0}
                                max={100}
                                step={1}
                                units={'px'}
                                value={round(shape.corner_radius)}
                                onlyPositive={true}
                            />
                        </GridCell>
                    }

                    {[RECTANGLE_TYPE, TEXT_TYPE].includes(shape.type) &&
                        <GridCell title={trans('text_padding')}>
                            <NumericInput
                                formName={formName}
                                fieldName={'text_padding'}
                                placeholder={'text_padding'}
                                shapeIndex={shape.index}
                                id={'text_padding'}
                                min={0}
                                max={100}
                                step={1}
                                units={'px'}
                                value={round(shape.text_padding)}
                                onlyPositive={true}
                            />
                        </GridCell>
                    }
                </Grid>
            </GridCell>

            {(
                [POLYGON_TYPE, BEZIER_TYPE, CURVED_TEXT_TYPE, LINE_TYPE, RECTANGLE_TYPE].includes(shape.type) ||
                (shape.type === TEXT_TYPE && shape.text_background_type === RECTANGLE_TYPE)
            ) &&
                <React.Fragment>
                    <GridCell style={{alignSelf: 'center'}}>{[LINE_TYPE].includes(shape.type) && `${trans('line_cap')}, `} {trans('line_join')}</GridCell>
                    <GridCell>
                        <Grid cols={'1fr 1fr'}>
                            {[LINE_TYPE].includes(shape.type) &&
                                <GridCell title={trans('line_cap')}>
                                    <ReactSelect
                                        value={selectedLinecap}
                                        options={linecapOptions}
                                        placeholder={trans(`line_cap_${DEFAULT_LINE_CAP}`)}
                                        onChange={(option) => change(formName, `shapes.${activeShapeId}.line_cap`, option?.value ?? null)}
                                        isClearable={true}
                                    />
                                </GridCell>
                            }
                            <GridCell title={trans('line_join')}>
                                <ReactSelect
                                    value={selectedLinejoin}
                                    options={linejoinOptions}
                                    placeholder={trans(`line_cap_${DEFAULT_LINE_JOIN}`)}
                                    onChange={(option) => change(formName, `shapes.${activeShapeId}.line_join`, option?.value ?? null)}
                                    isClearable={true}
                                />
                            </GridCell>
                        </Grid>
                    </GridCell>
                </React.Fragment>
            }
        </React.Fragment>
    )
}

export default ShapeStrokeForm